import React, { createRef, memo, useEffect, useRef } from 'react'
import { SrOnly, Text } from 'pcln-design-system'
import Cell from './Cell'
import getContextualAriaLabel from './getContextualAriaLabel'
import {
  endDateDelta,
  fullDateChanged,
  hoveredDateDelta,
  selectableDateDelta,
  startDateDelta,
} from '../utils/shouldDayUpdate'
import { IDayProps, IRenderDayFnProps } from '../types'

function Day({
  ariaLabel,
  customDayProps,
  dayWithRoundedCorners,
  dayWrapperStyleOverride,
  didUseKeyNavigation,
  endDate,
  fullDate,
  highlightedDates,
  hoveredDate,
  isTouch,
  maxDate,
  maxSelectableDate,
  minDate,
  showHoveredDates,
  startDate,
  width = 1 / 7,
  getCalendarLegendId,
  onDayClicked,
  onDayHovered,
  renderDayFn,
  setSelectedDayRef = () => {},
}: IDayProps) {
  const cellRef = createRef<HTMLDivElement>()
  const hasComponentInitialized = useRef(false)

  useEffect(() => {
    if (hasComponentInitialized.current && Number(fullDate) === Number(hoveredDate)) {
      cellRef.current.focus()
      setSelectedDayRef(cellRef.current)
    }

    hasComponentInitialized.current = true
  }, [fullDate, hoveredDate, setSelectedDayRef])

  if (!fullDate) {
    return <Cell py={2} width={width} />
  }

  const currentDate = parseInt(fullDate)

  const isStartDate = startDate && fullDate === startDate
  const isEndDate = endDate && fullDate === endDate
  const isAfterStart = startDate && fullDate > startDate
  const isBeforeEnd = endDate && currentDate < Number(endDate)
  const isHovered = hoveredDate && !endDate && fullDate <= hoveredDate
  const isValid = fullDate <= maxDate && fullDate >= minDate && fullDate <= maxSelectableDate
  const isSelected = isStartDate || isEndDate
  const isGrouped = isAfterStart && (isBeforeEnd || isHovered)
  const autobotId = `DATEPICKER_DAY_${fullDate}${!isValid ? '_INVALID' : ''}${isSelected ? '_SELECTED' : ''}${
    isGrouped ? '_GROUPED' : ''
  }`
  const day = fullDate.substring(6).replace(/^0/, '')

  const combinedCustomDayProps: IRenderDayFnProps = {
    customDayProps,
    day,
    fullDate,
    highlightedDates,
    isGrouped,
    isSelected,
    isValid,
  }

  const customDayComponent = typeof renderDayFn === 'function' ? renderDayFn(combinedCustomDayProps) : null
  const isCustomDayAvailable = customDayComponent && React.isValidElement(customDayComponent)

  const price = combinedCustomDayProps?.customDayProps?.price

  const calendarLegendId = getCalendarLegendId ? getCalendarLegendId({ date: fullDate }) : null
  const dayDescriptionId = `${fullDate}-calendar-day`
  const ariaDescribedBy = `${calendarLegendId} ${dayDescriptionId}`

  function onClick(evt) {
    if (isValid) {
      onDayClicked?.(evt, fullDate)
    }
  }

  function onMouseOver(evt) {
    if (isValid && showHoveredDates) {
      onDayHovered?.(evt, fullDate)
    }
  }

  const isHoveredDate = fullDate === hoveredDate
  const isMinDate = !hoveredDate && fullDate === minDate

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      aria-describedby={ariaDescribedBy}
      aria-pressed={(isValid && isSelected) || 'false'}
      role='button'
      className='day-cell'
      tabIndex={isMinDate || isHoveredDate ? 0 : -1}
      onClick={onClick}
      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events,
      onMouseOver={onMouseOver}
      ref={cellRef}
      style={
        dayWrapperStyleOverride || {
          display: 'inline-block',
          width: dayWithRoundedCorners ? `calc(${width * 100}% - 4px)` : `${width * 100}%`,
          marginLeft: 'auto',
          marginRight: 'auto',
        }
      }
    >
      <Cell
        data-autobot-element-id={autobotId}
        isTouch={isTouch}
        isCurrentHoverDate={didUseKeyNavigation && currentDate === Number(hoveredDate)}
        selected={isSelected}
        isStart={isStartDate}
        isEnd={isEndDate}
        grouped={isGrouped}
        valid={isValid}
        width={1}
        roundedCorners={dayWithRoundedCorners}
      >
        <SrOnly id={`${fullDate}-calendar-day`}>
          {getContextualAriaLabel({
            ariaLabel,
            endDate,
            isDateFocusable: isStartDate || isEndDate,
            price,
            startDate,
          })}
        </SrOnly>
        {isCustomDayAvailable ? (
          customDayComponent
        ) : (
          <Text bold fontWeight='bold' fontSize={2} py={2}>
            {day}
          </Text>
        )}
      </Cell>
    </div>
  )
}

export function areEqual(prevProps: IDayProps, nextProps: IDayProps): boolean {
  const { fullDate, startDate, endDate, hoveredDate, maxSelectableDate } = prevProps
  const {
    startDate: nextStartDate,
    endDate: nextEndDate,
    hoveredDate: nextHoveredDate,
    maxSelectableDate: nextMaxSelectableDate,
  } = nextProps

  if (startDate !== nextStartDate) {
    return false
  }

  if (endDate !== nextEndDate) {
    return false
  }

  if (fullDateChanged(fullDate, nextProps.fullDate)) {
    return false
  }

  if (!fullDate) {
    return true
  }

  const inStartDateDelta = startDateDelta(startDate, nextStartDate, fullDate, nextEndDate)
  const inEndDateDelta = endDateDelta(endDate, nextEndDate, fullDate, nextStartDate)
  const inHoveredDateDelta = hoveredDateDelta(hoveredDate, nextHoveredDate, fullDate)
  const inSelectableDateDelta = selectableDateDelta(maxSelectableDate, nextMaxSelectableDate, fullDate)

  const hasNewCustomDayProps = prevProps.customDayProps !== nextProps.customDayProps

  return (
    !hasNewCustomDayProps &&
    !inStartDateDelta &&
    !inEndDateDelta &&
    !inHoveredDateDelta &&
    !inSelectableDateDelta
  )
}

export default memo(Day, areEqual)
